import { Maybe } from "@figurepos/fgr-lib-business-logic/lib/common/types"
import { find, flatten, map, sortBy } from "ramda"
import type { StateObject } from "../../redux/types"
import { isAssignmentsAgendaOpened, isLocationsAndTerminalsAgendaOpened } from "../../data/selectors"
import { getCurrentPath, getRecordEditId } from "../../router/selectors"
import type { AssignmentFormItem } from "./AssignmentForm"
import { pipeline } from "../../../utils/functional"
import type { AssignmentsState } from "./reducer"
import type { Assignment, AssignmentConfig } from "../../../graphql/types"
import { getSettingByKeyAndLocationId } from "../settings/selectors"
import { getSelectedLocationId } from "../../common/locationsPicker/selectors"

export const getAssignmentStateObject = (state: StateObject): AssignmentsState => state.assignments

export const getAllAssignments = (state: StateObject): Assignment[] => getAssignmentStateObject(state).assignments || []

export const getAssignmentsByLocationId = (state: StateObject, locationId: string): Assignment[] => {
    return getAllAssignments(state).filter(a => a.locationId === locationId)
}

export const getAssignmentsBySelectedLocationId = (state: StateObject): Assignment[] => {
    const locationId = getSelectedLocationId(state)
    return locationId ? getAssignmentsByLocationId(state, locationId) : []
}

export const getAssignmentById = (state: StateObject, assignmentId: Maybe<string>): Maybe<Assignment> =>
    // @ts-expect-error
    pipeline(
        getAssignmentsBySelectedLocationId(state),
        // @ts-expect-error
        map(a => (a.children ? [a, ...a.children] : [a])),
        flatten,
        // @ts-expect-error
        find(a => a.id === assignmentId)
    )

export const getDataForAssignmentsTable = (state: StateObject): Assignment[] =>
    // @ts-expect-error
    pipeline(
        getAssignmentsBySelectedLocationId(state),
        // @ts-expect-error
        map(a => ({ ...a, children: a.children && sortBy(ch => ch.sortIndex, a.children) })),
        sortBy(a => a.sortIndex)
    )

export const getEditAssignmentId = (state: StateObject) => getRecordEditId(state)

export const getEditAssignmentAvailableParents = (state: StateObject): Assignment[] =>
    getAssignmentsBySelectedLocationId(state).filter(a => a.parentId == null && a.id !== getEditAssignmentId(state))

export const getEditAssignment = (state: StateObject): Maybe<Assignment> =>
    // @ts-expect-error
    pipeline(
        getAssignmentsBySelectedLocationId(state),
        // @ts-expect-error
        map(a => (a.children ? [a, ...a.children] : [a])),
        flatten,
        // @ts-expect-error
        find(a => a.id === getEditAssignmentId(state))
    )

const isConfigValueOverriden = (config: Maybe<AssignmentConfig>, key: string, compareValue: boolean) =>
    config ? config[key] !== compareValue : false

export const getAssignmentFormData = (state: StateObject): Maybe<AssignmentFormItem> => {
    const assignment = getEditAssignment(state)
    const selectedLocationId = getSelectedLocationId(state)

    if (!assignment || !selectedLocationId) {
        return null
    }

    const isCustomerRequiredSetting = getSettingByKeyAndLocationId(
        state,
        "APP_ORDER_CUSTOMER_REQUIRED",
        selectedLocationId
    )
    const isCustomerRequiredValue =
        isCustomerRequiredSetting == null || isCustomerRequiredSetting.value == null
            ? false
            : JSON.parse(isCustomerRequiredSetting.value)
    const isGuestCountRequiredSetting = getSettingByKeyAndLocationId(
        state,
        "APP_ORDER_GUEST_COUNT_REQUIRED",
        selectedLocationId
    )
    const isGuestCountRequiredValue =
        isGuestCountRequiredSetting == null || isGuestCountRequiredSetting.value == null
            ? false
            : JSON.parse(isGuestCountRequiredSetting.value)
    return {
        assignmentId: assignment.id,
        name: assignment.name,
        parentId: assignment.parentId,
        showAsQuickFilter: assignment.showAsQuickFilter,
        locationId: assignment.locationId,
        specialType: assignment.type,
        config: assignment.config,
        // @ts-expect-error
        children: assignment.children || [],
        isCustomerRequiredOverriden: isConfigValueOverriden(
            assignment.config,
            "customerRequired",
            isCustomerRequiredValue
        ),
        isGuestCountRequiredOverriden: isConfigValueOverriden(
            assignment.config,
            "guestCountRequired",
            isGuestCountRequiredValue
        ),
    }
}

export const isSection = (state: StateObject): boolean => {
    const edit = getEditAssignment(state)
    return (edit && edit.parentId == null) || getCurrentPath(state).query.add === "section"
}

export const shouldDownloadAssignmentsData = (state: StateObject): boolean =>
    isLocationsAndTerminalsAgendaOpened(state) ||
    (isAssignmentsAgendaOpened(state) && getAssignmentStateObject(state).assignments == null)
